import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, KeyRound, Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const DEMOS = [
  { role: "Student", email: "student@growthink.ai", icon: GraduationCap, blurb: "Learn, earn MWZ, collect certificates" },
  { role: "Teacher", email: "teacher@growthink.ai", icon: KeyRound, blurb: "Author courses and track gradebooks" },
  { role: "Administrator", email: "admin@growthink.ai", icon: Shield, blurb: "Platform analytics and on-chain audit" },
];

const HERO_IMG = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1400";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setBusy(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(
        user.role === "student"
          ? "/app/student"
          : user.role === "teacher"
          ? "/app/teacher"
          : "/app/admin"
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (demoEmail) => {
    setEmail(demoEmail);
    setPassword("Demo@123");
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-12 bg-background">
      {/* Left — branding panel */}
      <div className="relative hidden overflow-hidden lg:col-span-5 lg:flex lg:flex-col lg:justify-between">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="bg-hero-gradient absolute inset-0" />

        <div className="relative z-10 p-10 text-white">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo-link">
            <div className="bg-gold-gradient flex h-10 w-10 items-center justify-center rounded-lg shadow-md">
              <GraduationCap className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-xl font-bold tracking-tight">
              Mowanzi <span className="gold-text-gradient">Academy</span>
            </div>
          </Link>
        </div>

        <div className="relative z-10 max-w-md p-10 text-white">
          <div className="chip-gold mb-4 inline-flex">Welcome back</div>
          <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl">
            Your lifelong learning identity — anchored on-chain.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/80">
            Sign in to access your academic wallet, courses, blockchain
            certificates and the AI Tutor. Three demo personas are available for
            this preview.
          </p>
        </div>

        <div className="relative z-10 p-10 text-xs text-white/55">
          © 2026 · Pan-African Academic Blockchain Network
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center bg-warm p-6 lg:col-span-7">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 lg:hidden">
            <div className="bg-gold-gradient flex h-9 w-9 items-center justify-center rounded-lg">
              <GraduationCap className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg font-bold tracking-tight">
              Mowanzi Academy
            </div>
          </Link>

          <div className="chip-gold mb-3">Authenticate</div>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Sign in to your dashboard
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Demo password{" "}
            <code className="mono rounded bg-foreground px-2 py-0.5 text-primary">Demo@123</code>{" "}
            — pick a persona below to auto-fill.
          </p>

          <form onSubmit={doLogin} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="student@growthink.ai"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-lg border-border bg-white"
                data-testid="login-email-input"
                autoComplete="username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Demo@123"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-lg border-border bg-white"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="btn-gold h-12 w-full rounded-lg text-base font-semibold"
              data-testid="login-submit-btn"
            >
              {busy ? "Verifying…" : "Sign in"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <div className="mt-9">
            <div className="mb-3 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Or use a demo persona
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="space-y-2">
              {DEMOS.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fillDemo(d.email)}
                  className="group flex w-full items-center gap-4 rounded-lg border border-border bg-white p-4 text-left transition-all hover:border-primary/50 hover:shadow-md"
                  data-testid={`demo-${d.role.toLowerCase()}-btn`}
                >
                  <div className="bg-gold-gradient flex h-11 w-11 items-center justify-center rounded-lg shadow-md">
                    <d.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-foreground">{d.role}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{d.blurb}</div>
                    <div className="mono mt-1 text-[10px] text-primary">{d.email}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
